/*!
 * TinyPoker 分潤規則管理組件
 * 提供 revenue_share_rules 的 CRUD 功能
 * 支援階梯式(tiered)分潤規則的彈性設計
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Card, CardHeader, CardBody, CardTitle, Row, Col,
  Button, Table, Badge, Alert, Input, FormGroup, Label,
  Modal, ModalHeader, ModalBody, ModalFooter,
  InputGroup, InputGroupText,
} from "reactstrap";
import axios from "axios";

import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyStatStrip from "components/tiny/TinyStatStrip";
import TinyLoading, { TinyBusy } from "components/tiny/TinyLoading";
const API_BASE = "/api/tiny";

// ==================== 共用樣式 ====================
const COLORS = {
  cardBg: '#1e2530', headerBg: '#252d3a', inputBg: '#1a1f2b',
  border: 'rgba(255,255,255,0.1)', borderInput: 'rgba(255,255,255,0.2)',
  textPrimary: '#fff', textSecondary: 'rgba(255,255,255,0.8)',
  textMuted: 'rgba(255,255,255,0.5)', textDimmed: 'rgba(255,255,255,0.4)',
  accent: '#667eea', accentGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  dangerGradient: 'linear-gradient(135deg, #f5365c 0%, #f56036 100%)',
  success: '#2dce89', danger: '#f5365c', info: '#11cdef', warning: '#fb6340',
};

const inputStyle = { background: COLORS.inputBg, color: COLORS.textPrimary, border: `1px solid ${COLORS.borderInput}`, borderRadius: '6px' };
const labelStyle = { color: COLORS.textSecondary, fontWeight: 500, fontSize: '13px' };
const modalHeaderStyle = { background: COLORS.headerBg, color: COLORS.textPrimary, borderBottom: `1px solid ${COLORS.border}` };
const modalBodyStyle = { background: COLORS.cardBg, color: COLORS.textPrimary };
const modalFooterStyle = { background: COLORS.headerBg, borderTop: `1px solid ${COLORS.border}` };
const cancelBtnStyle = { background: 'rgba(149,165,166,0.2)', border: '1px solid rgba(149,165,166,0.5)', color: '#95a5a6', padding: '8px 20px', borderRadius: '6px' };
const saveBtnStyle = (d) => ({ background: d ? 'rgba(102,126,234,0.5)' : COLORS.accentGradient, border: 'none', padding: '8px 30px', borderRadius: '6px', fontWeight: 600, boxShadow: '0 4px 12px rgba(102,126,234,0.3)' });

const thStyle = { borderColor: COLORS.border, padding: '12px 10px', fontWeight: 600, fontSize: '13px' };
const tdStyle = { borderColor: 'rgba(255,255,255,0.05)', padding: '12px 10px', verticalAlign: 'middle' };

const SectionCard = ({ color, icon, title, children, style }) => (
  <div style={{ background: `rgba(${color}, 0.08)`, border: `1px solid rgba(${color}, 0.25)`, borderRadius: '8px', padding: '18px', marginBottom: '20px', ...style }}>
    {title && <h6 style={{ color: `rgb(${color})`, marginBottom: '15px', fontSize: '14px', fontWeight: 600 }}>{icon && <i className={`fa fa-${icon} mr-2`} />}{title}</h6>}
    {children}
  </div>
);

// 默認規則
const DEFAULT_RULE = {
  rule_key: "", rule_name: "", rule_type: "tiered", base_rate: 40,
  tiers: [{ min_weekly_rake: 0, max_weekly_rake: null, rate: 40 }],
  description: "", notes: "",
};

// 默認 Tier
const DEFAULT_TIER = { min_weekly_rake: 0, max_weekly_rake: null, rate: 0 };

function TinyRevenueRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingRule, setEditingRule] = useState({ ...DEFAULT_RULE });
  const [saving, setSaving] = useState(false);

  // Delete Modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);

  // Detail Modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailRule, setDetailRule] = useState(null);

  // ==================== 載入 ====================
  const loadRules = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API_BASE}/revenue-rules`);
      if (res.data.code === 1) setRules(res.data.data.rules || []);
      else throw new Error(res.data.msg);
    } catch (err) {
      setError("載入分潤規則失敗: " + (err.response?.data?.msg || err.message));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadRules(); }, [loadRules]);

  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(null), 3000); return () => clearTimeout(t); } }, [success]);

  // ==================== CRUD ====================
  const handleCreate = () => {
    setEditingRule({ ...DEFAULT_RULE, tiers: [{ ...DEFAULT_TIER }] });
    setModalMode("create"); setModalOpen(true);
  };

  const handleEdit = (rule) => {
    setEditingRule({
      ...DEFAULT_RULE,
      ...rule,
      tiers: rule.tiers && rule.tiers.length > 0 ? [...rule.tiers] : [{ ...DEFAULT_TIER }],
    });
    setModalMode("edit"); setModalOpen(true);
  };

  const handleViewDetail = (rule) => {
    setDetailRule(rule); setDetailModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      // 排序 tiers
      const sortedTiers = [...editingRule.tiers].sort((a, b) => (a.min_weekly_rake || 0) - (b.min_weekly_rake || 0));
      const ruleData = { ...editingRule, tiers: sortedTiers };

      if (modalMode === "create") {
        const res = await axios.post(`${API_BASE}/revenue-rules`, ruleData);
        if (res.data.code === 1) { setSuccess("分潤規則創建成功！"); setModalOpen(false); loadRules(); }
        else throw new Error(res.data.msg);
      } else {
        const res = await axios.put(`${API_BASE}/revenue-rules/${editingRule.rule_key}`, ruleData);
        if (res.data.code === 1) { setSuccess("分潤規則更新成功！"); setModalOpen(false); loadRules(); }
        else throw new Error(res.data.msg);
      }
    } catch (err) { setError("保存失敗: " + (err.response?.data?.msg || err.message)); }
    finally { setSaving(false); }
  };

  const handleDeleteClick = (rule) => { setRuleToDelete(rule); setDeleteModalOpen(true); };

  const handleDelete = async () => {
    if (!ruleToDelete) return;
    setSaving(true);
    try {
      const res = await axios.delete(`${API_BASE}/revenue-rules/${ruleToDelete.rule_key}`);
      if (res.data.code === 1) { setSuccess("分潤規則已刪除"); setDeleteModalOpen(false); setRuleToDelete(null); loadRules(); }
      else throw new Error(res.data.msg);
    } catch (err) { setError("刪除失敗: " + (err.response?.data?.msg || err.message)); }
    finally { setSaving(false); }
  };

  // ==================== Tier 管理 ====================
  const addTier = () => {
    const tiers = [...editingRule.tiers];
    const lastTier = tiers[tiers.length - 1];
    const newMin = lastTier ? (lastTier.max_weekly_rake ? lastTier.max_weekly_rake + 1 : (lastTier.min_weekly_rake || 0) + 50000) : 0;
    tiers.push({ min_weekly_rake: newMin, max_weekly_rake: null, rate: (lastTier?.rate || 40) + 5 });
    setEditingRule(p => ({ ...p, tiers }));
  };

  const removeTier = (idx) => {
    if (editingRule.tiers.length <= 1) return;
    const tiers = editingRule.tiers.filter((_, i) => i !== idx);
    setEditingRule(p => ({ ...p, tiers }));
  };

  const updateTier = (idx, field, value) => {
    const tiers = [...editingRule.tiers];
    tiers[idx] = { ...tiers[idx], [field]: value === "" ? null : Number(value) };
    setEditingRule(p => ({ ...p, tiers }));
  };

  const formatNumber = (n) => {
    if (n === null || n === undefined) return "無上限";
    return n.toLocaleString();
  };

  // ==================== Render ====================
  if (loading && rules.length === 0) {
    return (
      <div className="content">
        <TinyLoading text="載入中" variant="page" />
      </div>
    );
  }

  return (
    <div className="content">
      {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
      {success && <Alert color="success" toggle={() => setSuccess(null)}>{success}</Alert>}

      {/* 標題原本埋在「頂部卡片」裡，跟統計數字擠在同一張卡。
          ⚠️「新增規則」是**動作** → 依公版的收容標準不進 header，
             改放在 header 正下方靠右，視覺位置幾乎不變。 */}
      <TinyPageHeader
        title="分潤規則"
        description="管理所有分潤規則，支援階梯式、固定比例等彈性配置。"
      />

      {/* ⚠️ 三個計數原本是一張 Card 包三張漸層 Card，吃掉 140px。
             Brad 2026-09-10：縮小就好、不用移除 → 改用公版的 TinyStatStrip。
          ⚠️「新增規則」不再自己佔一行，搬到規則列表右上角跟刷新並排。 */}
      <TinyStatStrip items={[
        { label: '規則總數', icon: 'fa-sitemap', color: '102, 126, 234', iconColor: COLORS.accent, value: rules.length },
        { label: '階梯式規則', icon: 'fa-bar-chart', color: '45, 206, 137', iconColor: COLORS.success, value: rules.filter(r => r.rule_type === 'tiered').length },
        { label: '其他類型', icon: 'fa-sliders', color: '31, 142, 241', iconColor: '#1f8ef1', value: rules.filter(r => r.rule_type !== 'tiered').length },
      ]} />

      {/* 規則列表 */}
      <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}` }}>
        <CardHeader style={{ background: COLORS.headerBg, borderBottom: `1px solid ${COLORS.border}` }}>
          <div className="d-flex justify-content-between align-items-center">
            <CardTitle tag="h5" style={{ color: COLORS.textPrimary, marginBottom: 0, fontWeight: 600 }}>
              <i className="fa fa-list mr-2" style={{ color: COLORS.accent }} />規則列表
            </CardTitle>
            {/* Brad 2026-09-10：新增規則跟刷新並排，不要獨立佔一行。 */}
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <Button size="sm" onClick={loadRules} disabled={loading}
                style={{ background: 'rgba(102,126,234,0.15)', border: '1px solid rgba(102,126,234,0.4)', color: COLORS.accent, padding: '6px 16px', borderRadius: '6px', fontWeight: 500 }}>
                {loading ? <TinyBusy light style={{ marginRight: '5px' }} /> : <i className="fa fa-refresh mr-1" />}刷新
              </Button>
              <Button size="sm" onClick={handleCreate}
                style={{ background: COLORS.accentGradient, border: 'none', padding: '6px 18px', borderRadius: '6px', fontWeight: 600 }}>
                <i className="fa fa-plus mr-1" />新增規則
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody style={{ padding: '20px' }}>
          {rules.length === 0 ? (
            <div className="text-center p-5">
              <i className="fa fa-sitemap" style={{ fontSize: '4rem', color: 'rgba(255,255,255,0.12)', marginBottom: '20px', display: 'block' }} />
              <p style={{ color: COLORS.textMuted, fontSize: '1.1rem', marginBottom: '15px' }}>尚未設定分潤規則</p>
              <Button onClick={handleCreate} style={{ background: COLORS.accentGradient, border: 'none', padding: '8px 20px', borderRadius: '6px' }}>
                <i className="fa fa-plus mr-2" />新增第一個規則
              </Button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Table responsive hover size="sm" style={{ marginBottom: 0, color: COLORS.textSecondary }}>
                <thead style={{ background: COLORS.headerBg, color: COLORS.textPrimary }}>
                  <tr>
                    <th style={thStyle}>規則 Key</th>
                    <th style={thStyle}>規則名稱</th>
                    <th style={thStyle}>類型</th>
                    <th style={thStyle}>基礎比例</th>
                    <th style={thStyle}>階梯數</th>
                    <th style={thStyle}>階梯範圍</th>
                    <th style={thStyle}>說明</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule) => (
                    <tr key={rule.rule_key} style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                      <td style={tdStyle}>
                        <strong style={{ color: COLORS.textPrimary, fontSize: '13px' }}>{rule.rule_key}</strong>
                      </td>
                      <td style={tdStyle}><span style={{ fontWeight: 500 }}>{rule.rule_name}</span></td>
                      <td style={tdStyle}>
                        <Badge style={{ background: 'rgba(102,126,234,0.15)', border: '1px solid rgba(102,126,234,0.4)', color: COLORS.accent, padding: '4px 10px', fontSize: '11px', fontWeight: 600 }}>
                          {rule.rule_type === 'tiered' ? '階梯式' : rule.rule_type}
                        </Badge>
                      </td>
                      <td style={tdStyle}><span style={{ color: '#1f8ef1', fontWeight: 600 }}>{rule.base_rate ?? '-'}%</span></td>
                      <td style={tdStyle}>
                        <Badge style={{ background: 'rgba(45,206,137,0.2)', color: COLORS.success, padding: '4px 8px', fontSize: '12px', fontWeight: 600 }}>
                          {rule.tiers?.length || 0}
                        </Badge>
                      </td>
                      <td style={tdStyle}>
                        {rule.tiers && rule.tiers.length > 0 ? (
                          <small style={{ color: COLORS.textMuted, fontSize: '11px' }}>
                            {formatNumber(rule.tiers[0].min_weekly_rake)} ~ {formatNumber(rule.tiers[rule.tiers.length - 1].max_weekly_rake)}
                            <br />
                            <span style={{ color: '#1f8ef1' }}>{rule.tiers[0].rate}%</span> → <span style={{ color: COLORS.success }}>{rule.tiers[rule.tiers.length - 1].rate}%</span>
                          </small>
                        ) : '-'}
                      </td>
                      <td style={{ ...tdStyle, maxWidth: '200px' }}>
                        <small style={{ color: COLORS.textMuted }}>{rule.description?.substring(0, 40)}{rule.description?.length > 40 && '...'}</small>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Button size="sm" onClick={() => handleViewDetail(rule)} title="查看詳情"
                          style={{ background: 'rgba(102,126,234,0.15)', border: '1px solid rgba(102,126,234,0.4)', color: COLORS.accent, padding: '6px 10px', borderRadius: '5px', marginRight: '4px' }}>
                          <i className="fa fa-eye" />
                        </Button>
                        <Button size="sm" onClick={() => handleEdit(rule)} title="編輯"
                          style={{ background: 'rgba(17,205,239,0.15)', border: '1px solid rgba(17,205,239,0.4)', color: COLORS.info, padding: '6px 10px', borderRadius: '5px', marginRight: '4px' }}>
                          <i className="fa fa-edit" />
                        </Button>
                        <Button size="sm" onClick={() => handleDeleteClick(rule)} title="刪除"
                          style={{ background: 'rgba(245,54,92,0.15)', border: '1px solid rgba(245,54,92,0.4)', color: COLORS.danger, padding: '6px 10px', borderRadius: '5px' }}>
                          <i className="fa fa-trash" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          <div style={{ marginTop: '15px', color: COLORS.textMuted, fontSize: '13px' }}>
            共 {rules.length} 個分潤規則 {loading && <TinyBusy text="更新中" style={{ marginLeft: 8 }} />}
          </div>
        </CardBody>
      </Card>

      {/* ==================== 新增/編輯 Modal ==================== */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg" style={{ marginTop: '30px' }}>
        <ModalHeader toggle={() => setModalOpen(false)} style={modalHeaderStyle}>
          <i className={`fa fa-${modalMode === "create" ? "plus-circle" : "edit"} mr-2`} style={{ color: COLORS.accent }} />
          {modalMode === "create" ? "新增分潤規則" : `編輯規則 - ${editingRule.rule_key}`}
        </ModalHeader>
        <ModalBody style={{ ...modalBodyStyle, maxHeight: '70vh', overflowY: 'auto' }}>
          {/* 基本資訊 */}
          <SectionCard color="102, 126, 234" icon="info-circle" title="基本資訊">
            <Row>
              <Col md="6"><FormGroup>
                <Label style={labelStyle}>規則 Key <span style={{ color: COLORS.danger }}>*</span></Label>
                <Input type="text" value={editingRule.rule_key} onChange={(e) => setEditingRule(p => ({ ...p, rule_key: e.target.value }))}
                  disabled={modalMode === "edit"} placeholder="例如: super_agent_growth" style={{ ...inputStyle, ...(modalMode === "edit" ? { opacity: 0.6 } : {}) }} />
                <small style={{ color: COLORS.textMuted, fontSize: '11px' }}>唯一識別碼，使用英文與底線</small>
              </FormGroup></Col>
              <Col md="6"><FormGroup>
                <Label style={labelStyle}>規則名稱 <span style={{ color: COLORS.danger }}>*</span></Label>
                <Input type="text" value={editingRule.rule_name} onChange={(e) => setEditingRule(p => ({ ...p, rule_name: e.target.value }))} placeholder="例如: 超級代理成長級距" style={inputStyle} />
              </FormGroup></Col>
            </Row>
            <Row>
              <Col md="4"><FormGroup>
                <Label style={labelStyle}>規則類型</Label>
                <Input type="select" value={editingRule.rule_type} onChange={(e) => setEditingRule(p => ({ ...p, rule_type: e.target.value }))} style={inputStyle}>
                  <option value="tiered">階梯式 (Tiered)</option>
                  <option value="fixed">固定 (Fixed)</option>
                </Input>
              </FormGroup></Col>
              <Col md="4"><FormGroup>
                <Label style={labelStyle}>基礎比例 (%)</Label>
                <InputGroup>
                  <Input type="number" min="0" max="100" value={editingRule.base_rate ?? ""} onChange={(e) => setEditingRule(p => ({ ...p, base_rate: e.target.value === "" ? null : Number(e.target.value) }))} style={inputStyle} />
                  <InputGroupText style={{ background: COLORS.inputBg, color: COLORS.textMuted, border: `1px solid ${COLORS.borderInput}` }}>%</InputGroupText>
                </InputGroup>
                <small style={{ color: COLORS.textMuted, fontSize: '11px' }}>未達最低門檻時使用</small>
              </FormGroup></Col>
            </Row>
          </SectionCard>

          {/* 階梯配置 */}
          {editingRule.rule_type === 'tiered' && (
            <SectionCard color="45, 206, 137" icon="bar-chart" title="階梯配置">
              <div style={{ marginBottom: '15px' }}>
                <small style={{ color: COLORS.textMuted }}>
                  設定不同周水區間對應的分潤百分比。最後一個區間的「最高周水」留空表示無上限。
                </small>
              </div>
              
              {/* 階梯表格 */}
              <div style={{ overflowX: 'auto' }}>
                <Table size="sm" style={{ marginBottom: '15px', color: COLORS.textSecondary }}>
                  <thead style={{ background: COLORS.headerBg }}>
                    <tr>
                      <th style={{ ...thStyle, width: '50px', textAlign: 'center' }}>#</th>
                      <th style={thStyle}>最低周水</th>
                      <th style={thStyle}>最高周水</th>
                      <th style={thStyle}>分潤比例</th>
                      <th style={{ ...thStyle, width: '60px', textAlign: 'center' }}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editingRule.tiers.map((tier, idx) => (
                      <tr key={idx} style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, color: COLORS.textMuted }}>{idx + 1}</td>
                        <td style={tdStyle}>
                          <Input type="number" min="0" value={tier.min_weekly_rake ?? ""} onChange={(e) => updateTier(idx, 'min_weekly_rake', e.target.value)}
                            style={{ ...inputStyle, padding: '5px 10px', fontSize: '13px' }} />
                        </td>
                        <td style={tdStyle}>
                          <Input type="number" min="0" value={tier.max_weekly_rake ?? ""} onChange={(e) => updateTier(idx, 'max_weekly_rake', e.target.value)}
                            placeholder="無上限" style={{ ...inputStyle, padding: '5px 10px', fontSize: '13px' }} />
                        </td>
                        <td style={tdStyle}>
                          <InputGroup size="sm">
                            <Input type="number" min="0" max="100" value={tier.rate ?? ""} onChange={(e) => updateTier(idx, 'rate', e.target.value)}
                              style={{ ...inputStyle, padding: '5px 10px', fontSize: '13px' }} />
                            <InputGroupText style={{ background: COLORS.inputBg, color: COLORS.textMuted, border: `1px solid ${COLORS.borderInput}`, fontSize: '12px' }}>%</InputGroupText>
                          </InputGroup>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <Button size="sm" onClick={() => removeTier(idx)} disabled={editingRule.tiers.length <= 1}
                            style={{ background: 'rgba(245,54,92,0.15)', border: '1px solid rgba(245,54,92,0.3)', color: COLORS.danger, padding: '3px 8px', borderRadius: '4px', opacity: editingRule.tiers.length <= 1 ? 0.3 : 1 }}>
                            <i className="fa fa-minus" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <Button size="sm" onClick={addTier}
                style={{ background: 'rgba(45,206,137,0.15)', border: '1px solid rgba(45,206,137,0.4)', color: COLORS.success, padding: '6px 16px', borderRadius: '6px', fontWeight: 500 }}>
                <i className="fa fa-plus mr-1" /> 新增階梯
              </Button>

              {/* 階梯預覽 */}
              {editingRule.tiers.length > 0 && (
                <div style={{ marginTop: '20px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '15px' }}>
                  <h6 style={{ color: COLORS.textSecondary, fontSize: '13px', marginBottom: '10px' }}>
                    <i className="fa fa-eye mr-2" />階梯預覽
                  </h6>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {/* base_rate 區段 */}
                    {editingRule.tiers[0]?.min_weekly_rake > 0 && (
                      <div style={{ background: 'rgba(149,165,166,0.15)', border: '1px solid rgba(149,165,166,0.3)', borderRadius: '6px', padding: '8px 14px', fontSize: '12px' }}>
                        <div style={{ color: COLORS.textMuted }}>0 ~ {formatNumber((editingRule.tiers[0].min_weekly_rake || 1) - 1)}</div>
                        <div style={{ color: '#95a5a6', fontWeight: 600, fontSize: '14px' }}>{editingRule.base_rate}%</div>
                      </div>
                    )}
                    {editingRule.tiers.map((tier, idx) => (
                      <div key={idx} style={{ background: `rgba(${idx % 2 === 0 ? '31,142,241' : '45,206,137'}, 0.12)`, border: `1px solid rgba(${idx % 2 === 0 ? '31,142,241' : '45,206,137'}, 0.3)`, borderRadius: '6px', padding: '8px 14px', fontSize: '12px' }}>
                        <div style={{ color: COLORS.textMuted }}>{formatNumber(tier.min_weekly_rake)} ~ {formatNumber(tier.max_weekly_rake)}</div>
                        <div style={{ color: idx % 2 === 0 ? '#1f8ef1' : COLORS.success, fontWeight: 600, fontSize: '14px' }}>{tier.rate}%</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </SectionCard>
          )}

          {/* 說明與備註 */}
          <SectionCard color="251, 99, 64" icon="pencil" title="說明與備註" style={{ marginBottom: 0 }}>
            <FormGroup>
              <Label style={labelStyle}>說明</Label>
              <Input type="textarea" rows="2" value={editingRule.description || ""} onChange={(e) => setEditingRule(p => ({ ...p, description: e.target.value }))} placeholder="規則簡要說明..." style={inputStyle} />
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label style={labelStyle}>備註</Label>
              <Input type="textarea" rows="3" value={editingRule.notes || ""} onChange={(e) => setEditingRule(p => ({ ...p, notes: e.target.value }))} placeholder="詳細備註..." style={inputStyle} />
            </FormGroup>
          </SectionCard>
        </ModalBody>
        <ModalFooter style={modalFooterStyle}>
          <Button onClick={() => setModalOpen(false)} style={cancelBtnStyle}>取消</Button>
          <Button onClick={handleSave} disabled={saving} style={saveBtnStyle(saving)}>
            {saving ? <><TinyBusy light style={{ marginRight: 8 }} />儲存中</> : <><i className="fa fa-check mr-2" />保存</>}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ==================== 詳情 Modal ==================== */}
      <Modal isOpen={detailModalOpen} toggle={() => setDetailModalOpen(false)} size="lg" style={{ marginTop: '50px' }}>
        <ModalHeader toggle={() => setDetailModalOpen(false)} style={modalHeaderStyle}>
          <i className="fa fa-eye mr-2" style={{ color: COLORS.accent }} />
          規則詳情 - {detailRule?.rule_name}
        </ModalHeader>
        <ModalBody style={modalBodyStyle}>
          {detailRule && (
            <>
              <SectionCard color="102, 126, 234" icon="info-circle" title="基本資訊">
                <Row>
                  <Col md="4"><div style={{ marginBottom: '10px' }}><small style={{ color: COLORS.textMuted }}>規則 Key</small><div style={{ color: COLORS.textPrimary, fontWeight: 600 }}>{detailRule.rule_key}</div></div></Col>
                  <Col md="4"><div style={{ marginBottom: '10px' }}><small style={{ color: COLORS.textMuted }}>規則名稱</small><div style={{ color: COLORS.textPrimary, fontWeight: 600 }}>{detailRule.rule_name}</div></div></Col>
                  <Col md="2"><div style={{ marginBottom: '10px' }}><small style={{ color: COLORS.textMuted }}>類型</small><div><Badge style={{ background: 'rgba(102,126,234,0.2)', color: COLORS.accent, padding: '4px 10px' }}>{detailRule.rule_type === 'tiered' ? '階梯式' : detailRule.rule_type}</Badge></div></div></Col>
                  <Col md="2"><div style={{ marginBottom: '10px' }}><small style={{ color: COLORS.textMuted }}>基礎比例</small><div style={{ color: '#1f8ef1', fontWeight: 700, fontSize: '18px' }}>{detailRule.base_rate}%</div></div></Col>
                </Row>
              </SectionCard>

              {detailRule.tiers && detailRule.tiers.length > 0 && (
                <SectionCard color="45, 206, 137" icon="bar-chart" title={`階梯配置 (${detailRule.tiers.length} 個級距)`}>
                  <Table size="sm" style={{ marginBottom: 0, color: COLORS.textSecondary }}>
                    <thead style={{ background: COLORS.headerBg }}>
                      <tr>
                        <th style={{ ...thStyle, textAlign: 'center', width: '60px' }}>級距</th>
                        <th style={thStyle}>最低周水</th>
                        <th style={thStyle}>最高周水</th>
                        <th style={thStyle}>分潤比例</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailRule.tiers.map((tier, idx) => (
                        <tr key={idx} style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                          <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>{idx + 1}</td>
                          <td style={tdStyle}><span style={{ fontWeight: 500 }}>{formatNumber(tier.min_weekly_rake)}</span></td>
                          <td style={tdStyle}><span style={{ fontWeight: 500 }}>{formatNumber(tier.max_weekly_rake)}</span></td>
                          <td style={tdStyle}><span style={{ color: '#1f8ef1', fontWeight: 700, fontSize: '15px' }}>{tier.rate}%</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </SectionCard>
              )}

              {(detailRule.description || detailRule.notes) && (
                <SectionCard color="251, 99, 64" icon="pencil" title="說明與備註" style={{ marginBottom: 0 }}>
                  {detailRule.description && <div style={{ marginBottom: '10px' }}><small style={{ color: COLORS.textMuted }}>說明</small><p style={{ marginBottom: 0 }}>{detailRule.description}</p></div>}
                  {detailRule.notes && <div><small style={{ color: COLORS.textMuted }}>備註</small><p style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{detailRule.notes}</p></div>}
                </SectionCard>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter style={modalFooterStyle}>
          <Button onClick={() => setDetailModalOpen(false)} style={cancelBtnStyle}>關閉</Button>
          <Button onClick={() => { setDetailModalOpen(false); handleEdit(detailRule); }}
            style={{ background: 'rgba(17,205,239,0.15)', border: '1px solid rgba(17,205,239,0.4)', color: COLORS.info, padding: '8px 20px', borderRadius: '6px', fontWeight: 500 }}>
            <i className="fa fa-edit mr-2" />編輯此規則
          </Button>
        </ModalFooter>
      </Modal>

      {/* ==================== 刪除確認 Modal ==================== */}
      <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)} style={{ marginTop: '100px' }}>
        <ModalHeader toggle={() => setDeleteModalOpen(false)} style={modalHeaderStyle}>
          <i className="fa fa-exclamation-triangle mr-2" style={{ color: COLORS.danger }} />確認刪除
        </ModalHeader>
        <ModalBody style={modalBodyStyle}>
          {ruleToDelete && (
            <div>
              <div style={{ background: 'rgba(245,54,92,0.1)', border: '1px solid rgba(245,54,92,0.3)', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                <p style={{ marginBottom: 0, color: COLORS.textSecondary }}>
                  <i className="fa fa-warning mr-2" style={{ color: COLORS.danger }} />
                  確定要刪除分潤規則 <strong style={{ color: COLORS.textPrimary }}>{ruleToDelete.rule_name}</strong> 嗎？
                </p>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '15px' }}>
                <div style={{ marginBottom: '8px' }}><strong style={{ color: COLORS.textSecondary, fontSize: '13px' }}>規則 Key：</strong><span style={{ marginLeft: '10px' }}>{ruleToDelete.rule_key}</span></div>
                <div style={{ marginBottom: '8px' }}><strong style={{ color: COLORS.textSecondary, fontSize: '13px' }}>類型：</strong><span style={{ marginLeft: '10px' }}>{ruleToDelete.rule_type === 'tiered' ? '階梯式' : ruleToDelete.rule_type}</span></div>
                <div><strong style={{ color: COLORS.textSecondary, fontSize: '13px' }}>階梯數：</strong><span style={{ marginLeft: '10px' }}>{ruleToDelete.tiers?.length || 0}</span></div>
              </div>
              <div style={{ marginTop: '15px', background: 'rgba(251,99,64,0.08)', border: '1px solid rgba(251,99,64,0.2)', borderRadius: '8px', padding: '12px' }}>
                <small style={{ color: COLORS.warning }}>
                  <i className="fa fa-info-circle mr-2" />
                  如果此規則正被俱樂部或代理使用，將無法刪除。
                </small>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter style={modalFooterStyle}>
          <Button onClick={() => setDeleteModalOpen(false)} style={cancelBtnStyle}>取消</Button>
          <Button onClick={handleDelete} disabled={saving}
            style={{ background: COLORS.dangerGradient, border: 'none', padding: '8px 25px', borderRadius: '6px', fontWeight: 600, boxShadow: '0 4px 12px rgba(245,54,92,0.3)' }}>
            {saving ? <><TinyBusy light style={{ marginRight: 8 }} />刪除中</> : <><i className="fa fa-trash mr-2" />確認刪除</>}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default TinyRevenueRules;
